/**
 * shadcn/ui Button, hand-extended with a `loading` prop (ALC-3324 / ALC-3327).
 *
 * This is the approved exception to the "never edit `components/ui/`" rule:
 * the shared Button is the seam through which the design-system micro loader
 * (`GoldDustLoader`, the "Gold Dust" glyph) reaches every submitting button, so
 * consumers never hand-place a spinner again. Keep the rest of the file in
 * sync with upstream shadcn when regenerating.
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { GoldDustLoader } from '@/components/patterns/gold-dust-loader';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-control bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Busy state. Renders the "Gold Dust" glyph (`GoldDustLoader` sm) in
   * the leading icon slot, sets `disabled` and `aria-busy="true"`, and hides
   * the consumer's leading `<svg>` icon so the glyph takes its place and the
   * button keeps its width. The glyph uses `currentColor`, so it stays visible
   * on the gold primary button.
   *
   * With `asChild`, Radix `Slot` requires a single child, so only
   * `disabled`/`aria-busy` are applied — the glyph is not injected.
   */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const showGlyph = loading && !asChild;
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          showGlyph && '[&>svg:first-of-type]:hidden',
        )}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {showGlyph ? (
          <>
            <GoldDustLoader size="sm" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
